// API utilities for CHEKSTE
import axios from 'axios';

export const API_URL = process.env.REACT_APP_BACKEND_URL;

// Session token storage key
const SESSION_TOKEN_KEY = 'chekdeal_session_token';

// Get stored session token
export const getSessionToken = () => {
  try {
    return localStorage.getItem(SESSION_TOKEN_KEY);
  } catch (e) {
    return null;
  }
};

// Store session token (used after successful auth)
export const setSessionToken = (token) => {
  try {
    if (token) {
      localStorage.setItem(SESSION_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(SESSION_TOKEN_KEY);
    }
  } catch (e) {
    console.warn('Failed to store session token:', e);
  }
};

// Clear session token (used on logout)
export const clearSessionToken = () => {
  try {
    localStorage.removeItem(SESSION_TOKEN_KEY);
  } catch (e) {
    console.warn('Failed to clear session token:', e);
  }
};

const api = axios.create({
  baseURL: API_URL,
  // SAFARI FIX: Removed withCredentials because Emergent's infrastructure 
  // returns "Access-Control-Allow-Origin: *" which conflicts with credentials.
  // We now rely on Authorization header for auth (stored in localStorage).
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
  },
  // Timeout for Safari which can hang on failed requests
  timeout: 60000,
});

// Add Authorization header interceptor
// All auth is now handled via this header (Safari compatible)
api.interceptors.request.use((config) => {
  const token = getSessionToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Log PDF requests specifically for debugging
  if (config.url?.includes('/report/pdf')) {
    console.log('[API PDF Request]', {
      url: config.url,
      method: config.method,
      responseType: config.responseType,
      hasAuth: !!token,
      headers: config.headers
    });
  }
  return config;
}, (error) => {
  console.error('[API Request Error]', error);
  return Promise.reject(error);
});

// Response interceptor for better error handling on Safari
api.interceptors.response.use(
  (response) => {
    // Log PDF responses specifically for debugging
    if (response.config?.url?.includes('/report/pdf')) {
      console.log('[API PDF Response]', {
        status: response.status,
        statusText: response.statusText,
        dataType: typeof response.data,
        dataConstructor: response.data?.constructor?.name,
        contentType: response.headers?.['content-type'],
        dataSize: response.data?.size || response.data?.length || 'unknown'
      });
    }
    return response;
  },
  (error) => {
    // Log detailed error info for debugging
    console.error('[API Response Error]', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message,
      code: error.code
    });
    
    // Handle network errors (common on Safari with CORS issues)
    if (!error.response && error.message === 'Network Error') {
      console.error('[Network Error] Request failed - possible CORS or connectivity issue');
    }
    
    return Promise.reject(error);
  }
);

// Auth endpoints
export const authAPI = {
  getMe: () => api.get('/api/auth/me'),
  logout: () => {
    clearSessionToken();
    return api.post('/api/auth/logout');
  },
  checkAccess: (feature) => api.get(`/api/auth/check-access/${feature}`),
  // createSession now also stores the token for Safari compatibility
  createSession: async (data) => {
    const response = await api.post('/api/auth/session', data);
    // Store the session token in localStorage for Safari ITP workaround
    // The backend returns the token in the response for this purpose
    if (response.data?.session_token) {
      setSessionToken(response.data.session_token);
    }
    return response;
  },
};

// Analysis endpoints
export const analysisAPI = {
  analyze: (data) => api.post('/api/analyze', data),
  analyzeDemo: (data) => api.post('/api/analyze/demo', data),
  // The two frozen examples on the Analyzer page: a file read, no quota, no RentCast, no save
  // The example is stored prose, so the language has to be asked for: without it the
  // server serves the English file and a Spanish visitor reads English under Spanish
  // headings. Defaults to English so existing callers are unaffected.
  getSampleDeal: (kind, language = 'en') =>
    api.get(`/api/sample-deals/${kind}`, { params: { language } }),
  autocomplete: (query) => api.get(`/api/address/autocomplete?query=${encodeURIComponent(query)}`),
  recalculate: (data) => api.post('/api/analyze/recalculate', data),
  reclassify: (data) => api.post('/api/analyze/reclassify', data),
  refreshMarketData: (propertyKey, address) => api.post('/api/analyze/refresh-market-data', { property_key: propertyKey, address }),
  generatePDF: (analysis, language = 'en') => api.post('/api/report/pdf', { analysis, language }, { responseType: 'blob' }),
  sendReportEmail: (analysis, recipientEmails, language = 'en') => api.post('/api/report/email', { analysis, recipient_emails: recipientEmails, language }),
  // NEW: Fetch property details without analysis (for confirmation step)
  fetchProperty: (inputType, inputValue) => api.post('/api/property/fetch', { input_type: inputType, input_value: inputValue }),
  // V5.0: Get analysis by ID (for URL-based loading)
  // V2.3.0: Added lang parameter to regenerate deal_explanation in correct language
  getAnalysis: (analysisId, lang = null) => api.get(`/api/analysis/${analysisId}${lang ? `?lang=${lang}` : ''}`),
};

// Deals endpoints
export const dealsAPI = {
  save: (data) => api.post('/api/deals/save', data),
  getSaved: (userId) => api.get(`/api/deals/saved?user_id=${userId}`),
  deleteSaved: (savedDealId, userId) => api.delete(`/api/deals/saved/${savedDealId}?user_id=${userId}`),
  compare: (data) => api.post('/api/deals/compare', data),
  getComparisons: (userId) => api.get(`/api/deals/comparisons?user_id=${userId}`),
};

// Recent Analyses endpoints (auto-history / draft tracking)
export const recentAnalysesAPI = {
  addRecent: (data) => api.post('/api/deals/recent', data),
  getRecent: (userId, limit = 20) => api.get(`/api/deals/recent?user_id=${userId}&limit=${limit}`),
  markAsSaved: (data) => api.patch('/api/deals/recent/mark-saved', data),
  updateState: (data) => api.patch('/api/deals/recent/update-state', data),
  deleteRecent: (recentId, userId) => api.delete(`/api/deals/recent/${recentId}?user_id=${userId}`),
};

// How the deal ended — sold / refinanced with a new appraisal / did not close.
// Owner only; the response carries the comparison against the saved ARV.
export const analysisOutcomeAPI = {
  report: (analysisId, payload) => api.patch(`/api/analyses/${analysisId}/outcome`, payload),
};

// Inbox endpoints
export const inboxAPI = {
  getInbox: (userId, status = 'all') => api.get(`/api/inbox?user_id=${userId}&status=${status}`),
  getSubmission: (submissionId, userId) => api.get(`/api/inbox/submission/${submissionId}?user_id=${userId}`),
  createUsername: (data) => api.post('/api/inbox/username', data),
  submit: (username, data) => api.post(`/api/inbox/${username}/submit`, data),
  // public: the inbox owner's display name, for the form's heading
  getRecipient: (username) => api.get(`/api/inbox/${username}/recipient`),
  updateStatus: (submissionId, data) => api.put(`/api/inbox/submission/${submissionId}/status`, data),
  updateNotes: (submissionId, data) => api.put(`/api/inbox/submission/${submissionId}/notes`, data),
  sendInboxLink: (data) => api.post('/api/inbox/send-link', data),
};

// Share endpoints
export const shareAPI = {
  shareAnalysis: (data) => api.post('/api/analysis/share', data),
  getSharedAnalysis: (analysisId) => api.get(`/api/analysis/shared/${analysisId}`),
  // NEW: Create a share snapshot with CURRENT state (not original saved analysis)
  createShareSnapshot: (currentState) => api.post('/api/analysis/share/snapshot', { analysis: currentState }),
};

// Billing endpoints
export const billingAPI = {
  createCheckout: (data) => api.post('/api/billing/checkout', data),
  getSubscription: () => api.get('/api/billing/subscription'),
  checkStatus: (sessionId) => api.get(`/api/billing/checkout/status/${sessionId}`),
  cancel: () => api.post('/api/billing/cancel', {}),  // user self-cancel (at period end)
};

// User Comparables endpoints
export const compsAPI = {
  getComps: (analysisId) => api.get(`/api/comps/${analysisId}`),
  addComp: (data) => api.post('/api/comps/add', data),
  updateComp: (compId, data) => api.put(`/api/comps/${compId}`, data),
  deleteComp: (compId) => api.delete(`/api/comps/${compId}`),
  recalculateARV: (data) => api.post('/api/comps/recalculate-arv', data),
  // G: make one of the STORED ARV modes the active one — no recomputation.
  setARVMode: (analysisId, mode) => api.post('/api/comps/arv-mode', { analysis_id: analysisId, mode }),
};

// Usage endpoints
export const usageAPI = {
  getUsage: (userId) => api.get(`/api/usage?user_id=${userId}`),
  getStatus: (userId) => api.get(`/api/usage/status/${userId}`),
};

// Investment Criteria endpoints
export const investmentCriteriaAPI = {
  getCriteria: () => api.get('/api/user/investment-criteria'),
  updateCriteria: (data) => api.put('/api/user/investment-criteria', data),
  resetCriteria: () => api.post('/api/user/investment-criteria/reset'),
  completeOnboarding: () => api.post('/api/user/complete-criteria-onboarding'),
};

// Feedback endpoints
export const feedbackAPI = {
  shouldPrompt: (userId, analysesCount) => 
    api.get(`/api/feedback/should-prompt/${userId}?analyses_count=${analysesCount}`),
  submit: (data) => api.post('/api/feedback/submit', data),
  dismiss: (userId) => api.post(`/api/feedback/dismiss/${userId}`),
};

// Contact form endpoint
export const contactAPI = {
  submit: (data) => api.post('/api/contact/submit', data),
};

// Subscription/Plan endpoints
export const subscriptionAPI = {
  getUsageInfo: (userId) => api.get(`/api/subscription/usage/${userId}`),
  startTrial: (userId) => api.post(`/api/subscription/start-trial/${userId}`),
};

// Analytics/Conversion Tracking endpoints
export const analyticsAPI = {
  // Get user stats
  getUserStats: (userId) => api.get(`/api/analytics/user?user_id=${userId}`),
  getDealBreakdown: (userId) => api.get(`/api/analytics/user/${userId}/deal-breakdown`),
  getPlatformStats: () => api.get('/api/analytics/platform'),
  
  // Conversion tracking
  trackUpgradeClick: (userId, source, planType, context = {}) => 
    api.post(`/api/analytics/track/upgrade-click?user_id=${userId}&source=${source}&plan_type=${planType}`, context),
  
  trackLimitReached: (userId, planType, analysesUsed, analysesLimit) =>
    api.post(`/api/analytics/track/limit-reached?user_id=${userId}&plan_type=${planType}&analyses_used=${analysesUsed}&analyses_limit=${analysesLimit}`),
  
  trackValueShown: (userId, valueType, valueMessage, analysesCount) =>
    api.post(`/api/analytics/track/value-shown?user_id=${userId}&value_type=${valueType}&value_message=${encodeURIComponent(valueMessage)}&analyses_count=${analysesCount}`),
  
  trackUpgradePromptShown: (userId, promptType, location, trigger) =>
    api.post(`/api/analytics/track/upgrade-prompt-shown?user_id=${userId}&prompt_type=${promptType}&location=${location}&trigger=${trigger}`),
  
  // Admin
  getConversionStats: () => api.get('/api/analytics/conversion-stats'),
};

// Admin Profitability endpoints
// Provider benchmark (admin, behind PROVIDER_BENCHMARK): RentCast | RealEstateAPI | appraiser
export const providerBenchmarkAPI = {
  subjects: () => api.get('/api/admin/provider-benchmark/appraisals'),
  run: (addresses) => api.post('/api/admin/provider-benchmark', { addresses }, { timeout: 600000 }),
};

export const adminProfitabilityAPI = {
  verifyAccess: () => api.get('/api/admin/verify'),
  getSummary: (month) => api.get(`/api/admin/profitability/summary${month ? `?month=${month}` : ''}`),
  getPlanProfitability: (month) => api.get(`/api/admin/profitability/plans${month ? `?month=${month}` : ''}`),
  getUsers: (month, minCost = 0, limit = 100) => 
    api.get(`/api/admin/profitability/users?min_cost=${minCost}&limit=${limit}${month ? `&month=${month}` : ''}`),
  getUserDetail: (userId, month) => 
    api.get(`/api/admin/profitability/user/${userId}${month ? `?month=${month}` : ''}`),
  getAlerts: (month, level) => 
    api.get(`/api/admin/profitability/alerts${month ? `?month=${month}` : ''}${level ? `&level=${level}` : ''}`),
  getHeavyUsers: (month) => api.get(`/api/admin/profitability/heavy-users${month ? `?month=${month}` : ''}`),
  getBlockedUsers: (month) => api.get(`/api/admin/profitability/blocked-users${month ? `?month=${month}` : ''}`),
  getBlockedRequests: (month, limit = 100) => 
    api.get(`/api/admin/profitability/blocked-requests?limit=${limit}${month ? `&month=${month}` : ''}`),
  recalculateAll: (month) => api.post(`/api/admin/profitability/recalculate${month ? `?month=${month}` : ''}`),
};

// Subscriber registry — every user (profiles-based), real MRR. Founder-only.
export const adminSubscribersAPI = {
  list: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString();
    return api.get(`/api/admin/subscribers${qs ? `?${qs}` : ''}`);
  },
  cancel: (userId) => api.post(`/api/admin/subscription/cancel/${userId}`, {}),  // founder cancels a user (period end)
};

// Access Codes endpoints
export const codesAPI = {
  redeem: (code) => api.post('/api/codes/redeem', { code }),
  adminGenerate: (data) => api.post('/api/admin/codes/generate', data),
  adminGenerateAndSend: (data) => api.post('/api/admin/codes/generate-and-send', data),
  adminList: () => api.get('/api/admin/codes/list'),
  adminRevoke: (code) => api.post('/api/admin/codes/revoke', { code }),
};

export default api;
